import { useRef, useState, useEffect } from 'react';
import {
  Flex,
  Text,
  Select,
  Input,
  Box,
  InputGroup,
  InputRightElement,
  IconButton,
  List,
  ListItem,
} from '@chakra-ui/react';
import { CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';

interface BranchOption {
  id: number;
  label: string;
}

interface BrokerOption {
  email: string;
  label: string;
}

interface AnalyticsFiltersProps {
  yearMonth: string;
  onYearMonthChange: (value: string) => void;
  branchOptions?: BranchOption[];
  selectedBranchId?: number | null;
  onBranchChange?: (id: number | null) => void;
  brokerOptions?: BrokerOption[];
  selectedBrokerEmail?: string | null;
  onBrokerChange?: (email: string | null) => void;
  onReset?: () => void;
  period: string;
  onPeriodChange?: (value: string) => void;
}

/** Generate the last 13 months (current + 12 previous) as YYYY-MM options */
function getMonthOptions(): { label: string; value: string }[] {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 13; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-CA', { month: 'long', year: 'numeric' });
    options.push({ label, value });
  }
  return options;
}

const monthOptions = getMonthOptions();

function BranchCombobox({
  options,
  selectedId,
  onChange,
}: {
  options: BranchOption[];
  selectedId: number | null | undefined;
  onChange: (id: number | null) => void;
}) {
  const selectedLabel = options.find((o) => o.id === selectedId)?.label ?? '';
  const [search, setSearch] = useState(selectedLabel);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync search text when external selection changes
  useEffect(() => {
    setSearch(
      selectedId ? (options.find((o) => o.id === selectedId)?.label ?? '') : '',
    );
  }, [selectedId, options]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        // Reset search to current selection on blur
        setSearch(
          selectedId
            ? (options.find((o) => o.id === selectedId)?.label ?? '')
            : '',
        );
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedId, options]);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (opt: BranchOption) => {
    onChange(opt.id);
    setSearch(opt.label);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearch('');
    setOpen(false);
  };

  return (
    <Box ref={containerRef} position="relative" w="240px">
      <InputGroup size="sm">
        <Input
          value={search}
          placeholder="All Branches"
          borderRadius="8px"
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          pr={selectedId ? '56px' : '32px'}
          fontSize="sm"
        />
        <InputRightElement w={selectedId ? '56px' : '32px'}>
          <Flex gap="2px">
            {selectedId && (
              <IconButton
                aria-label="Clear branch"
                icon={<CloseIcon boxSize="8px" />}
                size="xs"
                variant="ghost"
                onClick={handleClear}
              />
            )}
            <IconButton
              aria-label="Open branch list"
              icon={<ChevronDownIcon />}
              size="xs"
              variant="ghost"
              onClick={() => setOpen((o) => !o)}
            />
          </Flex>
        </InputRightElement>
      </InputGroup>

      {open && filtered.length > 0 && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
          boxShadow="md"
          maxH="220px"
          overflowY="auto"
        >
          <List spacing={0}>
            <ListItem
              px="12px"
              py="8px"
              fontSize="sm"
              color="gray.500"
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              onClick={handleClear}
            >
              All Branches
            </ListItem>
            {filtered.map((opt) => (
              <ListItem
                key={opt.id}
                px="12px"
                py="8px"
                fontSize="sm"
                cursor="pointer"
                fontWeight={opt.id === selectedId ? '700' : '400'}
                bg={opt.id === selectedId ? 'brand.50' : 'white'}
                color={opt.id === selectedId ? 'brand.600' : 'gray.800'}
                _hover={{ bg: opt.id === selectedId ? 'brand.50' : 'gray.50' }}
                onClick={() => handleSelect(opt)}
              >
                {opt.label}
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {open && filtered.length === 0 && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
          boxShadow="md"
          p="12px"
        >
          <Text fontSize="sm" color="gray.400">
            No branches found
          </Text>
        </Box>
      )}
    </Box>
  );
}

function BrokerCombobox({
  options,
  selectedEmail,
  onChange,
}: {
  options: BrokerOption[];
  selectedEmail: string | null | undefined;
  onChange: (email: string | null) => void;
}) {
  const selectedLabel =
    options.find((o) => o.email === selectedEmail)?.label ?? '';
  const [search, setSearch] = useState(selectedLabel);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearch(
      selectedEmail
        ? (options.find((o) => o.email === selectedEmail)?.label ?? '')
        : '',
    );
  }, [selectedEmail, options]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch(
          selectedEmail
            ? (options.find((o) => o.email === selectedEmail)?.label ?? '')
            : '',
        );
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedEmail, options]);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (opt: BrokerOption) => {
    onChange(opt.email);
    setSearch(opt.label);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearch('');
    setOpen(false);
  };

  return (
    <Box ref={containerRef} position="relative" w="240px">
      <InputGroup size="sm">
        <Input
          value={search}
          placeholder="All Brokers"
          borderRadius="8px"
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          pr={selectedEmail ? '56px' : '32px'}
          fontSize="sm"
        />
        <InputRightElement w={selectedEmail ? '56px' : '32px'}>
          <Flex gap="2px">
            {selectedEmail && (
              <IconButton
                aria-label="Clear broker"
                icon={<CloseIcon boxSize="8px" />}
                size="xs"
                variant="ghost"
                onClick={handleClear}
              />
            )}
            <IconButton
              aria-label="Open broker list"
              icon={<ChevronDownIcon />}
              size="xs"
              variant="ghost"
              onClick={() => setOpen((o) => !o)}
            />
          </Flex>
        </InputRightElement>
      </InputGroup>

      {open && filtered.length > 0 && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
          boxShadow="md"
          maxH="220px"
          overflowY="auto"
        >
          <List spacing={0}>
            <ListItem
              px="12px"
              py="8px"
              fontSize="sm"
              color="gray.500"
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              onClick={handleClear}
            >
              All Brokers
            </ListItem>
            {filtered.map((opt) => (
              <ListItem
                key={opt.email}
                px="12px"
                py="8px"
                fontSize="sm"
                cursor="pointer"
                fontWeight={opt.email === selectedEmail ? '700' : '400'}
                bg={opt.email === selectedEmail ? 'brand.50' : 'white'}
                color={opt.email === selectedEmail ? 'brand.600' : 'gray.800'}
                _hover={{
                  bg: opt.email === selectedEmail ? 'brand.50' : 'gray.50',
                }}
                onClick={() => handleSelect(opt)}
              >
                {opt.label}
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {open && filtered.length === 0 && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
          boxShadow="md"
          p="12px"
        >
          <Text fontSize="sm" color="gray.400">
            No brokers found
          </Text>
        </Box>
      )}
    </Box>
  );
}

export const AnalyticsFilters = ({
  yearMonth,
  onYearMonthChange,
  branchOptions,
  selectedBranchId,
  onBranchChange,
  brokerOptions,
  selectedBrokerEmail,
  onBrokerChange,
  onReset,
  period,
  onPeriodChange,
}: AnalyticsFiltersProps) => {
  const showBranchSelector =
    onBranchChange && branchOptions && branchOptions.length > 1;
  const showBrokerSelector =
    onBrokerChange && brokerOptions && brokerOptions.length > 0;

  return (
    <Flex gap="12px" align="center" wrap="wrap" mb="20px">
      <Flex align="center" gap="8px">
        <Text fontWeight="500" fontSize="sm" whiteSpace="nowrap">
          Month
        </Text>
        <Select
          value={onPeriodChange && period !== 'monthly' ? period : yearMonth}
          onChange={(e) => {
            const value = e.target.value;
            if (onPeriodChange && (value === 'ytd' || value === 'r12')) {
              onPeriodChange(value);
            } else {
              onYearMonthChange(value);
            }
          }}
          size="sm"
          w="180px"
          borderRadius="8px"
        >
          {onPeriodChange && (
            <>
              <option value="ytd">YTD</option>
              <option value="r12">Rolling 12 Months</option>
            </>
          )}
          {monthOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Flex>

      {showBranchSelector && (
        <Flex align="center" gap="8px">
          <Text fontWeight="500" fontSize="sm" whiteSpace="nowrap">
            Branch
          </Text>
          <BranchCombobox
            options={branchOptions!}
            selectedId={selectedBranchId}
            onChange={onBranchChange!}
          />
        </Flex>
      )}

      {showBrokerSelector && (
        <Flex align="center" gap="8px">
          <Text fontWeight="500" fontSize="sm" whiteSpace="nowrap">
            Broker
          </Text>
          <BrokerCombobox
            options={brokerOptions!}
            selectedEmail={selectedBrokerEmail}
            onChange={onBrokerChange!}
          />
        </Flex>
      )}

      {onReset && (
        <Box
          as="button"
          onClick={onReset}
          fontSize="xs"
          fontWeight="600"
          color="gray.500"
          border="1px solid"
          borderColor="gray.300"
          borderRadius="6px"
          px="10px"
          py="5px"
          _hover={{ borderColor: 'gray.400', color: 'gray.700' }}
        >
          Reset
        </Box>
      )}
    </Flex>
  );
};
